import { useEffect, useCallback, useMemo } from "react";

import moment from "moment-timezone";
import { useWatch } from "react-hook-form";

import { combineDateTime } from "../../sharedUtils/helper";

export function useHourCreateSideEffects({
  setValue,
  isRecurrenceShowed = false,
  control,
  type,
  baseName = ""
}) {
  const getFormName = useCallback(
    (_inputName) => baseName + _inputName,
    [baseName]
  );
  const formNames = useMemo(
    () =>
      [
        "dateRange",
        "startTime",
        "endTime",
        "recurrenceRepetitionDays",
        "startDateTime",
        "endDateTime",
        "lunchStartDate",
        "lunchEndDate"
      ].reduce(
        (acc, _inputName) => ({
          ...acc,
          [_inputName]: getFormName(_inputName)
        }),
        {
          dateRange: "dateRange",
          startTime: "startTime",
          endTime: "endTime",
          recurrenceRepetitionDays: "recurrenceRepetitionDays",
          startDateTime: "startDateTime",
          endDateTime: "endDateTime",
          lunchStartDate: "lunchStartDate",
          lunchEndDate: "lunchEndDate"
        }
      ),
    [getFormName]
  );
  const dateRange = useWatch({
    name: formNames.dateRange,
    control
  });
  const startTime = useWatch({
    name: formNames.startTime,
    control
  });
  const endTime = useWatch({
    name: formNames.endTime,
    control
  });
  const recurrenceRepetitionDays = useWatch({
    name: formNames.recurrenceRepetitionDays,
    control
  });
  const startDateTime = useWatch({
    name: formNames.startDateTime,
    control
  });
  const endDateTime = useWatch({
    name: formNames.endDateTime,
    control
  });

  useEffect(() => {
    // Until a period is selected, weekly rows use the dates carried by the times.
    if (
      type === "weekly" &&
      baseName &&
      !dateRange?.startDate &&
      !dateRange?.endDate
    ) {
      if (!moment(startDateTime).isSame(startTime) && (startDateTime || startTime))
        setValue(formNames.startDateTime, startTime || null);
      if (!moment(endDateTime).isSame(endTime) && (endDateTime || endTime))
        setValue(formNames.endDateTime, endTime || null);
      return;
    }

    if (dateRange?.startDate && dateRange?.endDate) {
      const newStartDateTime = combineDateTime(dateRange.startDate, startTime);
      const newEndDateTime = combineDateTime(dateRange.endDate, endTime);
      if (
        newStartDateTime &&
        !moment(startDateTime).isSame(newStartDateTime, "s")
      )
        setValue(formNames.startDateTime, newStartDateTime);
      if (
        newEndDateTime &&
        !moment(endDateTime).isSame(newEndDateTime, "s")
      )
        setValue(formNames.endDateTime, newEndDateTime);
    }
  }, [
    dateRange, startTime, endTime, formNames, startDateTime, endDateTime,
    type, baseName
  ]);

  // set startDate day in recurrence days if it is not selected
  useEffect(() => {
    if (isRecurrenceShowed && dateRange?.startDate && type !== "weekly") {
      const startDateMoment = moment(dateRange.startDate);

      const startDateDay = startDateMoment.day();
      if (!recurrenceRepetitionDays?.includes(startDateDay)) {
        const newDate = recurrenceRepetitionDays
          ? recurrenceRepetitionDays.concat(startDateDay)
          : [startDateDay];
        setValue("recurrenceRepetitionDays", newDate);
      }
    }
  }, [dateRange, isRecurrenceShowed, recurrenceRepetitionDays, type]);
}
